import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShoppingBag, Plus, Minus, Trash2, CreditCard, ArrowLeft } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import analyticsService from '../../services/analyticsService';

const PLACEHOLDER =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iIzM3NDE1MSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';

function itemLabel(item) {
  return item.title || item.name || 'Item';
}

const Cart = () => {
  const navigate = useNavigate();
  const {
    cartItems = [],
    updateQuantity,
    removeFromCart,
    clearCart,
    getTotalPrice,
    getTotalItems,
    cartLoaded,
  } = useCart();

  useEffect(() => {
    if (cartLoaded && cartItems.length > 0) {
      analyticsService.trackCartView(cartItems, getTotalPrice());
    }
  }, [cartLoaded, cartItems, getTotalPrice]);

  const handleCheckout = () => {
    navigate('/checkout');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95), rgba(31, 41, 55, 0.95))',
        padding: '100px 20px 48px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#06b6d4',
              textDecoration: 'none',
              fontSize: '15px',
              fontWeight: 500,
            }}
          >
            <ArrowLeft size={18} />
            Back to shop
          </Link>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '28px',
          }}
        >
          <ShoppingBag style={{ width: '32px', height: '32px', color: '#ec4899' }} />
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 700,
              margin: 0,
              background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Your cart {cartLoaded ? `(${getTotalItems()})` : ''}
          </h1>
        </div>

        {!cartLoaded ? (
          <p style={{ color: '#9ca3af' }}>Loading cart…</p>
        ) : cartItems.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 24px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <ShoppingBag
              style={{ width: '64px', height: '64px', color: '#6b7280', marginBottom: '16px' }}
            />
            <h2 style={{ color: 'white', fontSize: '20px', margin: '0 0 8px' }}>Your cart is empty</h2>
            <p style={{ color: '#9ca3af', marginBottom: '24px' }}>Add something wonderful, then come back to checkout.</p>
            <Link
              to="/gallery"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Browse gallery
            </Link>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {cartItems.map((item) => {
                const label = itemLabel(item);
                const isDonation = item.type === 'donation';
                return (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '20px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <img
                      src={item.image || item.src || PLACEHOLDER}
                      alt={label}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        flexShrink: 0,
                      }}
                      onError={(e) => {
                        e.currentTarget.src = PLACEHOLDER;
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '16px',
                          marginBottom: '6px',
                          wordBreak: 'break-word',
                        }}
                      >
                        {label}
                        {item.type === 'event' && (
                          <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>· Event</span>
                        )}
                      </div>
                      {item.type === 'donation' ? (
                        <span style={{ color: '#f9a8d4', fontSize: '14px' }}>
                          Donation · ${Number(item.price).toFixed(2)}
                        </span>
                      ) : (
                        <span style={{ color: '#10b981', fontSize: '18px', fontWeight: 700 }}>
                          ${Number(item.price).toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {!isDonation && (
                        <>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: '#ef4444',
                              cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer',
                              opacity: item.quantity <= 1 ? 0.5 : 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Minus size={16} />
                          </button>
                          <span style={{ color: 'white', fontWeight: 700, minWidth: '24px', textAlign: 'center' }}>
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              background: 'rgba(16, 185, 129, 0.2)',
                              border: '1px solid rgba(16, 185, 129, 0.3)',
                              color: '#10b981',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Plus size={16} />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFromCart(item.id)}
                        aria-label={`Remove ${label}`}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          background: 'rgba(156, 163, 175, 0.2)',
                          border: '1px solid rgba(156, 163, 175, 0.3)',
                          color: '#9ca3af',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                marginTop: '28px',
                padding: '24px',
                background: 'rgba(0, 0, 0, 0.4)',
                borderRadius: '16px',
                border: '1px solid rgba(6, 182, 212, 0.3)',
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <span style={{ color: '#10b981', fontSize: '26px', fontWeight: 700 }}>
                  Total: ${getTotalPrice().toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  type="button"
                  onClick={handleCheckout}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    fontWeight: 700,
                    padding: '16px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <CreditCard size={20} />
                  Go to checkout
                </button>
                <button
                  type="button"
                  onClick={clearCart}
                  style={{
                    width: '100%',
                    background: 'rgba(156, 163, 175, 0.2)',
                    border: '1px solid rgba(156, 163, 175, 0.3)',
                    color: '#9ca3af',
                    fontWeight: 500,
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Clear cart
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
